import Helper, { DateFormat } from "@/services/Helper";
import { ContactModel } from "./ClientModel";
import { JobModel } from "./RequestModel";
import { WorkOrderItemModel } from "./WorkOrderItemModel";

// Phone number entry for AdditionalData
interface PhoneEntry {
    number: string;
    type: 'mobile' | 'landline';
}

// Email entry for AdditionalData
interface EmailEntry {
    email: string;
}

// Additional data structure for WorkOrder
interface WorkOrderAdditionalData {
    phoneNumbers?: PhoneEntry[];
    emails?: EmailEntry[];
    [key: string]: any; // Allow other dynamic fields
}

class WorkOrderModel
{
    constructor(obj?: WorkOrderModel)
    {
        if (obj)
        {
            Object.assign(this, obj);

            // Store date strings in private backing fields
            if (obj.DateCreated)
            {
                this._DateCreated = obj.DateCreated + "";
            }

            if (obj.DateDue)
            {
                this._DateDue = obj.DateDue + "";
            }

            if (obj.DateCompleted)
            {
                this._DateCompleted = obj.DateCompleted + "";
            }

            if (obj.DatePaid)
            {
                this._DatePaid = obj.DatePaid + "";
            }

            // Handle relationships
            if (obj.Request)
            {
                this.Request = new JobModel(obj.Request);
                // Ensure RequestId is set from the Request object if not already set
                if (!this.RequestId && this.Request.Id)
                {
                    this.RequestId = this.Request.Id;
                }
            }

            if (obj.Contact)
            {
                this.Contact = new ContactModel(obj.Contact);
            }

            if (obj.Items)
            {
                this.Items = obj.Items.map(item => new WorkOrderItemModel(item));
            }

            // Parse AdditionalData from JSON string if present
            if (obj.AdditionalData)
            {
                if (typeof obj.AdditionalData === 'string')
                {
                    try {
                        this.AdditionalData = JSON.parse(obj.AdditionalData);
                    } catch {
                        this.AdditionalData = null;
                    }
                }
                else
                {
                    this.AdditionalData = obj.AdditionalData;
                }
            }
        }
        else
        {
            // Set defaults for new records
            this._DateCreated = Helper.dateToInput(new Date(), DateFormat.Date);
            this.Status = 1;
            this.Items = [];
        }
    }

    public Id?: number | null;
    public RequestId?: number | null;
    public ContactId?: number | null;

    // Relationships
    public Request?: JobModel | null;
    public Contact?: ContactModel | null;
    public Items?: WorkOrderItemModel[] | null;

    // Date fields
    private _DateCreated?: string | null;

    public get DateCreated(): string | null
    {
        return Helper.dateToInput(this.DateCreatedAsDate, DateFormat.Date) || null;
    }

    public set DateCreated(val: string | null)
    {
        this._DateCreated = val || null;
    }

    public get DateCreatedAsDate(): Date | null
    {
        if (this._DateCreated)
        {
            return Helper.dateFromString(this._DateCreated) || null;
        }
        return null;
    }

    private _DateDue?: string | null;

    public get DateDue(): string | null
    {
        return Helper.dateToInput(this.DateDueAsDate, DateFormat.Date) || null;
    }

    public set DateDue(val: string | null)
    {
        this._DateDue = val || null;
    }

    public get DateDueAsDate(): Date | null
    {
        if (this._DateDue)
        {
            return Helper.dateFromString(this._DateDue) || null;
        }
        return null;
    }

    private _DateCompleted?: string | null;

    public get DateCompleted(): string | null
    {
        return Helper.dateToInput(this.DateCompletedAsDate, DateFormat.Date) || null;
    }

    public set DateCompleted(val: string | null)
    {
        this._DateCompleted = val || null;
    }

    public get DateCompletedAsDate(): Date | null
    {
        if (this._DateCompleted)
        {
            return Helper.dateFromString(this._DateCompleted) || null;
        }
        return null;
    }

    private _DatePaid?: string | null;

    public get DatePaid(): string | null
    {
        return Helper.dateToInput(this.DatePaidAsDate, DateFormat.Date) || null;
    }

    public set DatePaid(val: string | null)
    {
        this._DatePaid = val || null;
    }

    public get DatePaidAsDate(): Date | null
    {
        if (this._DatePaid)
        {
            return Helper.dateFromString(this._DatePaid) || null;
        }
        return null;
    }

    // Financial fields
    public Credit?: number | null;
    public Subtotal?: number | null;
    public Tax?: number | null;
    public Total?: number | null;
    public AmountPaid?: number | null;

    // Reference numbers
    public InvoiceNumber?: string | null;
    public PONumber?: string | null;
    public EstimateNumber?: string | null;
    public Title?: string | null;
    public Terms?: string | null;
    public SpecialInstructions?: string | null;
    public InternalNotes?: string | null;

    // Status
    public Status?: number | null;
    public PaymentStatus?: string | null;

    // Request-specific fields
    public RequestTitle?: string | null;
    public Description?: string | null;
    public Priority?: string | null;
    public Location?: string | null;
    public Asset?: string | null;

    // Additional Request-specific fields
    public DepartmentCategory?: string | null;
    public RequestMethod?: string | null;
    public NotifyUpdates?: boolean;
    public Requestor?: string | null;
    public AffectedUser?: string | null;
    public Email?: string | null;
    public Phone?: string | null;
    public Comments?: string | null;
    public AdditionalData?: WorkOrderAdditionalData | null;

    // Owner (Technician ID) and Author (Technician ID who created)
    public Owner?: number | null;
    public Author?: number | null;

    // Related Request (links Invoice/Partial to a specific Request WorkOrder)
    public RelatedWorkOrderId?: number | null;

    // Soft delete flag (set by API when IncludeDeleted is true)
    public IsDeleted?: boolean;

    // Computed totals from items
    public get CalculatedSubtotal(): number
    {
        if (!this.Items || this.Items.length === 0) return 0;
        return this.Items.reduce((sum, item) => sum + (item.LineTotal || 0), 0);
    }

    public get CalculatedTotal(): number
    {
        const subtotal = this.Subtotal || this.CalculatedSubtotal;
        const tax = this.Tax || 0;
        const credit = this.Credit || 0;
        return subtotal + tax - credit;
    }
}

class WorkOrderFilter
{
    public Id?: number[];
    public RequestId?: number[];
    public ContactId?: number[];
    public Status?: number[];
    public Priority?: string[];
    public PaymentStatus?: string;
    public Title?: string;
    public Titles?: string[];
    public TitleNot?: string;
    public SearchTerm?: string;
    public StartDate?: Date;
    public EndDate?: Date;
    public IncludeRequest?: boolean = false;
    public IncludeContact?: boolean = false;
    public IncludeItems?: boolean = false;
    public IncludeDeleted?: boolean = false;
    public Owner?: number[];
    public Department?: string[];
    public Page?: number = 0;
    public Size?: number = 25;
    public OrderBy?: string;
}

export { WorkOrderModel, WorkOrderFilter }
export type { PhoneEntry, EmailEntry, WorkOrderAdditionalData }
